import type { AppDef } from "../../shell/nav";
import { AnonymizePage } from "./AnonymizePage";
import { ApprovalsPage } from "./ApprovalsPage";
import { CatalogPage } from "./CatalogPage";
import { CustomFieldsPage } from "./CustomFieldsPage";
import { EmployeeDetailPage } from "./EmployeeDetailPage";
import { EmployeesPage } from "./EmployeesPage";
import { EquipmentPage } from "./EquipmentPage";
import { ExternalMailPage } from "./ExternalMailPage";
import { GroupDetailPage } from "./GroupDetailPage";
import { GroupsPage } from "./GroupsPage";
import { ImportPage } from "./ImportPage";
import { LoansPage } from "./LoansPage";
import { MonthlyPage } from "./MonthlyPage";
import { OrgChartPage } from "./OrgChartPage";
import { OrgUnitsPage } from "./OrgUnitsPage";
import { PositionsPage } from "./PositionsPage";
import { ExternalRequestsPage } from "./ExternalRequestsPage";
import { RequestDetailPage } from "./RequestDetailPage";
import { RequestListPage } from "./RequestListPage";
import { NewRequestPage } from "./NewRequestPage";
import {
  LeavesRedirect,
  OffboardingRedirect,
  OnboardingRedirect,
  TransferRedirect,
  UpdatesRedirect,
} from "./RequestRedirects";
import { ReportingPage } from "./ReportingPage";
import { TasksPage } from "./TasksPage";
import { WfReportPage } from "./WfReportPage";
import { WorkflowDesignPage } from "./WorkflowDesignPage";

/**
 * ナビの群は `ux1a-ia.md`(UX1-a)の並び: 従業員 / 申請と承認 / 貸与品 / 統制 / 設定。
 * 項目の path は旧 Jinja 画面のまま(変えるとブックマークとテストが壊れる)。
 */
export const HR: AppDef = {
  key: "hr",
  name: "人事",
  defaultPath: "employees",
  groups: [
    {
      label: "従業員",
      items: [
        { path: "employees", label: "従業員一覧", phase: "R2", component: EmployeesPage, feature: "hr.directory" },
        { path: "org", label: "組織図", phase: "R2", component: OrgChartPage, feature: "hr.directory" },
        { path: "org-reporting", label: "指揮系統図", phase: "R2", component: ReportingPage, feature: "hr.directory" },
      ],
    },
    {
      label: "申請と承認",
      items: [
        { path: "requests/list", label: "申請一覧", phase: "R2", component: RequestListPage, feature: "hr.workflow" },
        // 入社・退社・異動・変更・休職・復職の 5 入口を 1 つに畳んだ(UX1-b2)。`?kind=` で種別を前置き
        { path: "requests/new", label: "新しい申請", phase: "R2", component: NewRequestPage, feature: "hr.workflow" },
        { path: "approvals", label: "承認待ち", phase: "R2", component: ApprovalsPage, feature: "hr.workflow" },
        { path: "tasks", label: "タスク", phase: "R2", component: TasksPage, feature: "hr.workflow" },
        {
          path: "requests",
          label: "社外依頼",
          phase: "R2",
          component: ExternalRequestsPage,
          feature: "hr.workflow",
        },
      ],
    },
    {
      label: "貸与品",
      items: [
        { path: "loans", label: "貸与台帳", phase: "R2", component: LoansPage, feature: "hr.loans" },
        { path: "equipment", label: "備品マスタ", phase: "R2", component: EquipmentPage, feature: "hr.loans" },
      ],
    },
    {
      label: "統制",
      items: [
        { path: "monthly", label: "月次人員棚卸", phase: "R2", component: MonthlyPage, feature: "hr.directory" },
        { path: "report", label: "レポート", phase: "R2", component: WfReportPage, feature: "hr.report" },
        { path: "groups", label: "動的グループ", phase: "HR3", component: GroupsPage, feature: "hr.groups" },
        { path: "anonymize", label: "匿名化", phase: "R2", component: AnonymizePage, feature: "hr.anonymize" },
      ],
    },
    {
      label: "設定",
      items: [
        { path: "workflow", label: "WF設計", phase: "R2", component: WorkflowDesignPage, feature: "hr.workflow" },
        { path: "catalog", label: "カタログ", phase: "R2", component: CatalogPage, feature: "hr.workflow" },
        { path: "org-units", label: "組織マスタ", phase: "R2", component: OrgUnitsPage, feature: "hr.directory" },
        { path: "org-positions", label: "役職マスタ", phase: "R2", component: PositionsPage, feature: "hr.directory" },
        { path: "custom-fields", label: "カスタム項目", phase: "R2", component: CustomFieldsPage, feature: "hr.directory" },
        {
          path: "external-mail",
          label: "社外依頼メール",
          phase: "HR3",
          component: ExternalMailPage,
          feature: "hr.workflow",
          // 取得 API が hr:admin 限定。ロールを付けないと viewer に
          // 「閲覧だけできる」と出しておいて閲覧すらできない(Codex 低 1)
          role: "hr:admin",
        },
        { path: "import", label: "HR取込", phase: "R2", component: ImportPage, feature: "hr.directory" },
      ],
    },
  ],
  extraRoutes: [
    { path: "employees/:employeeId", component: EmployeeDetailPage },
    // 申請の詳細。ナビの requests は社外依頼なので、別の道にする
    { path: "wf-requests/:requestId", component: RequestDetailPage },
    { path: "groups/:groupId", component: GroupDetailPage },
    // 旧 5 入口のブックマーク救済 → /hr/requests/new?kind=
    { path: "onboarding", component: OnboardingRedirect },
    { path: "offboarding", component: OffboardingRedirect },
    { path: "transfer", component: TransferRedirect },
    { path: "leaves", component: LeavesRedirect },
    { path: "updates", component: UpdatesRedirect },
  ],
};
